import { Sparkles, RefreshCw, Loader2, Volume2, Play, Pause } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BIO_TONES_FALLBACK = [
  { id: "mythic", label: "Mythic" },
  { id: "streetwise", label: "Streetwise" },
];

/**
 * Turnaround character bio card — bio text + tone chips + ElevenLabs voice-over row + clone-my-voice CTA.
 * All state lives in Studio.jsx; this is a pure presentation component.
 */
export const BioCard = ({
  current,
  tones,
  bioTone,
  bioRerolling,
  onReroll,
  voices,
  voice,
  onVoiceChange,
  ttsLoading,
  isPlaying,
  onPlay,
  onPause,
  audioSrc,
  audioRef,
  onOpenVoiceClone,
  onAudioPlay,
  onAudioPause,
  onAudioEnded,
}) => {
  if (!current?.bio) return null;
  return (
    <div className="mt-5 p-5 rounded-2xl bg-gradient-to-br from-[#120E15] via-[#120E15] to-[#0d1f24] border border-[#F2C94C]/25" data-testid="bio-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="font-mono-hy text-[10px] text-[#F2C94C] flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> BIO · AUTO-GENERATED
          {current.bio_tone && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30 text-[9px] uppercase">
              {current.bio_tone}
            </span>
          )}
        </div>
        <button
          onClick={() => onReroll(bioTone)}
          disabled={bioRerolling}
          data-testid="bio-refresh-btn"
          className="w-8 h-8 rounded-full flex items-center justify-center border border-[#F2C94C]/30 hover:border-[#F2C94C] text-[#F2C94C] transition-all disabled:opacity-50"
          title="Reroll bio in current tone"
        >
          {bioRerolling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Tone chips */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(tones || BIO_TONES_FALLBACK).map((t) => {
          const active = bioTone === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onReroll(t.id)}
              disabled={bioRerolling}
              data-testid={`bio-tone-${t.id}`}
              className={`font-mono-hy text-[10px] px-3 py-1.5 rounded-full border transition-all ${
                active
                  ? "text-[#0A0A0A]"
                  : "text-white hover:text-white border-[#F2C94C]/20 hover:border-[#F2C94C]/50"
              } disabled:opacity-50`}
              style={active ? { background: t.accent, borderColor: t.accent } : undefined}
              title={t.hint}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <p className="font-body text-sm text-white leading-relaxed" data-testid="bio-text">{current.bio}</p>

      {/* Voice-over row */}
      <div className="mt-4 pt-4 border-t border-[#F2C94C]/10 flex items-center gap-3 flex-wrap">
        <div className="font-mono-hy text-[10px] text-[#00E5FF] flex items-center gap-1.5">
          <Volume2 className="w-3.5 h-3.5" /> VOICE-OVER · ElevenLabs
        </div>
        <Select value={voice} onValueChange={onVoiceChange}>
          <SelectTrigger data-testid="voice-select" className="bg-[#0A0A0A] border-[#00E5FF]/25 h-8 w-48 text-xs font-body">
            <SelectValue placeholder="Pick a voice" />
          </SelectTrigger>
          <SelectContent className="bg-[#120E15] border-[#00E5FF]/25">
            {(voices?.length ? voices : (tones || BIO_TONES_FALLBACK)).map((v) => (
              <SelectItem key={v.id} value={v.id} className="text-xs font-body">{v.label || v.id}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!isPlaying ? (
          <button
            onClick={onPlay}
            disabled={ttsLoading}
            data-testid="bio-play-btn"
            className="font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#00E5FF] text-[#0A0A0A] hover:bg-[#7BF0FF] transition-all disabled:opacity-60 flex items-center gap-2"
          >
            {ttsLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Narrating…</> : <><Play className="w-3.5 h-3.5" /> Play Narration</>}
          </button>
        ) : (
          <button
            onClick={onPause}
            data-testid="bio-pause-btn"
            className="font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all flex items-center gap-2"
          >
            <Pause className="w-3.5 h-3.5" /> Pause
          </button>
        )}
        {isPlaying && (
          <div className="flex items-end gap-0.5 h-6" data-testid="voice-wave">
            {[...Array(8)].map((_, i) => (
              <span key={`bar-${i}`} className="w-1 bg-[#00E5FF] rounded-full animate-pulse"
                style={{ height: `${25 + ((i * 13) % 40)}%`, animationDelay: `${i * 90}ms`, animationDuration: `700ms` }} />
            ))}
          </div>
        )}
      </div>
      <audio
        ref={audioRef}
        src={audioSrc || undefined}
        onPlay={onAudioPlay}
        onPause={onAudioPause}
        onEnded={onAudioEnded}
        hidden
        data-testid="bio-audio-el"
      />

      {/* Voice cloning entry point */}
      <div className="mt-3 pt-3 border-t border-[#F2C94C]/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="font-mono-hy text-[9px] text-white">
          Want narration in your own voice?
        </div>
        <button
          onClick={onOpenVoiceClone}
          data-testid="voice-clone-open-btn"
          className="font-mono-hy text-[10px] px-3 py-1.5 rounded-full border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60]/10 hover:border-[#E27D60] transition-all flex items-center gap-1.5"
        >
          <Sparkles className="w-3 h-3" /> Clone My Voice
        </button>
      </div>
    </div>
  );
};
